// @flow
import React, { Component } from 'react';
import { isBrowser } from 'react-device-detect';
import Button from 'components/common/Button';
import { type Profile } from 'store/modules/profile';
import { BaseActions } from 'store/actionCreators';

import './SettingProfile.scss';

// 연락처 자동 하이픈 추가 함수
const formatPhoneNumber = value => {
  // 숫자만 추출
  const numbers = value.replace(/[^\d]/g, '');

  // 길이에 따라 포맷팅
  if (numbers.length <= 2) {
    return numbers;
  }

  // 지역번호 패턴 확인 (02, 031, 032, 033, 041, 042, 043, 044, 051, 052, 053, 054, 055, 061, 062, 063, 064)
  if (numbers.startsWith('02')) {
    // 서울 지역번호 (02-XXX-XXXX 또는 02-XXXX-XXXX)
    if (numbers.length <= 2) {
      return numbers;
    }
    if (numbers.length <= 5) {
      return `${numbers.slice(0, 2)}-${numbers.slice(2)}`;
    }
    if (numbers.length <= 9) {
      return `${numbers.slice(0, 2)}-${numbers.slice(2, 5)}-${numbers.slice(5)}`;
    }
    if (numbers.length <= 10) {
      return `${numbers.slice(0, 2)}-${numbers.slice(2, 6)}-${numbers.slice(6)}`;
    }
    // 10자리 초과시 10자리까지만 사용
    return `${numbers.slice(0, 2)}-${numbers.slice(2, 6)}-${numbers.slice(6, 10)}`;
  }

  // 기타 지역번호 (03X, 04X, 05X, 06X - XXX-XXX-XXXX 또는 XXX-XXXX-XXXX)
  if (/^(03[1-9]|04[1-4]|05[1-5]|06[1-4])/.test(numbers)) {
    if (numbers.length <= 3) {
      return numbers;
    }
    if (numbers.length <= 6) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    }
    if (numbers.length <= 10) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}`;
    }
    if (numbers.length <= 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    }
    // 11자리 초과시 11자리까지만 사용
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  }

  // 휴대폰 번호 (010, 011, 016, 017, 018, 019)
  if (/^(010|011|016|017|018|019)/.test(numbers)) {
    if (numbers.length <= 3) {
      return numbers;
    }
    if (numbers.length <= 7) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    }
    if (numbers.length <= 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    }
    // 11자리 초과시 11자리까지만 사용
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  }

  // 기본 패턴 (알 수 없는 번호)
  if (numbers.length <= 3) {
    return numbers;
  }
  if (numbers.length <= 7) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  }
  if (numbers.length <= 11) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
  }
  // 11자리 초과시 11자리까지만 사용
  return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
};

// 연락처 유효성 검증 함수
const isValidPhoneNumber = value => {
  // 숫자만 추출
  const numbers = value.replace(/[^\d]/g, '');
  
  // 최소 9자리 이상이어야 함 (02-123-4567 형태)
  if (numbers.length < 9) {
    return false;
  }
  
  // 휴대폰 번호는 11자리
  if (/^(010|011|016|017|018|019)/.test(numbers)) {
    return numbers.length === 11;
  }
  
  // 서울 지역번호는 9~10자리
  if (numbers.startsWith('02')) {
    return numbers.length >= 9 && numbers.length <= 10;
  }
  
  // 기타 지역번호는 10~11자리
  if (/^(03[1-9]|04[1-4]|05[1-5]|06[1-4])/.test(numbers)) {
    return numbers.length >= 10 && numbers.length <= 11;
  }
  
  // 기본적으로 9~11자리 허용
  return numbers.length >= 9 && numbers.length <= 11;
};

// 사업자등록번호 자동 하이픈 추가 함수 (xxx-xx-xxxxx)
const formatBusinessNumber = value => {
  // 숫자만 추출
  const numbers = value.replace(/[^\d]/g, '');

  // 길이에 따라 포맷팅
  if (numbers.length <= 3) {
    return numbers;
  }
  if (numbers.length <= 5) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  }
  if (numbers.length <= 10) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5)}`;
  }
  // 10자리 초과시 10자리까지만 사용
  return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5, 10)}`;
};

type Props = {
  profile: Profile,
  user: any,
  onUpdateProfile: ({
    managerName: string,
    displayName: string,
    shortBio: string,
    mobile: string,
    bizNumber: string,
  }) => any,
  onUploadThumbnail: () => any,
  onUploadBizFile: () => any,
};

type State = {
  editing: boolean,
  managerName: string,
  displayName: string,
  shortBio: string,
  mobile: string,
  bizNumber: string,
};

class SettingProfile extends Component<Props, State> {
  state = {
    editing: false,
    managerName: '',
    displayName: '',
    mobile: '',
    bizNumber: '',
  };

  constructor(props: Props) {
    super(props);
    const { profile } = this.props;
    this.state.managerName = profile.manager_name;
    this.state.displayName = profile.display_name;
    this.state.shortBio = profile.short_bio;
    this.state.mobile = profile.mobile;
    this.state.bizNumber = profile.biz_number;
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevProps.profile !== this.props.profile || (!prevState.editing && this.state.editing)) {
      const { manager_name, display_name, mobile, biz_number } = this.props.profile;
      this.setState({
        managerName: manager_name,
        displayName: display_name,
        mobile,
        bizNumber: biz_number,
      });
    }
  }

  onToggleEdit = (e: SyntheticEvent<*>) => {
    e.preventDefault();
    // this.setState({
    //   editing: !this.state.editing,
    // });
  };
  onChange = (e: SyntheticInputEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let formattedValue = value;

    // 연락처 필드에 자동 하이픈 적용
    if (name === 'mobile') {
      formattedValue = formatPhoneNumber(value);
    }

    // 사업자등록번호 필드에 자동 하이픈 적용
    if (name === 'bizNumber') {
      formattedValue = formatBusinessNumber(value);
    }

    this.setState({
      [name]: formattedValue,
    });
  };

  onSubmit = async (e: SyntheticEvent<*>) => {
    e.preventDefault();
    const { managerName, displayName, mobile, bizNumber } = this.state;

    // 연락처 유효성 검증
    if (mobile && !isValidPhoneNumber(mobile)) {
      BaseActions.showToast({
        message: '연락처가 유효하지 않습니다. 올바른 연락처를 입력해주세요.',
        type: 'error',
      });
      return;
    }

    try {
      await this.props.onUpdateProfile({
        managerName,
        displayName,
        mobile,
        bizNumber,
      });
      // this.setState({
      //   editing: false,
      // });
      BaseActions.showToast({
        message: '프로필이 저장되었습니다.',
        type: 'success',
      });
    } catch (err) {
      console.log(err);
    }
  };

  render() {
    const { profile, onUploadThumbnail, onUploadBizFile } = this.props;
    const { editing, managerName, displayName, shortBio, mobile, bizNumber } = this.state;
    return (
      <div className="SettingProfile">
        <div className="profile-card">
          <div className="profile-header">
            <h2>프로필 설정</h2>
            <p>회원 정보를 관리하고 업데이트하세요</p>
          </div>

          <form onSubmit={this.onSubmit}>
            <div className="form-section">
              <div className="form-group">
                <span className="form-label">회원등급</span>
                <div className="form-display">
                  <span className="grade-badge">{this.props.user.grade.name}</span>
                </div>
              </div>

              <div className="form-group">
                <span className="form-label">이메일</span>
                <div className="form-display">{this.props.user.email}</div>
              </div>
            </div>

            <div className="form-section">
              <div className="form-group">
                <label className="form-label" htmlFor="mobile">
                  연락처
                  <input
                    id="mobile"
                    className="form-input"
                    onChange={this.onChange}
                    name="mobile"
                    placeholder="연락처를 입력해주세요."
                    value={mobile}
                    maxLength="40"
                    required
                  />
                </label>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="managerName">
                  이름
                  <input
                    id="managerName"
                    className="form-input"
                    onChange={this.onChange}
                    name="managerName"
                    placeholder="이름을 입력해주세요."
                    value={managerName}
                    maxLength="25"
                    required
                  />
                </label>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="displayName">
                  상호/법인명/대학교명
                  <input
                    id="displayName"
                    className="form-input"
                    onChange={this.onChange}
                    name="displayName"
                    placeholder="주식회사 데이터씨, 서울대학교"
                    value={displayName}
                    maxLength="25"
                    required
                  />
                </label>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="bizNumber">
                  사업자등록번호
                  <input
                    id="bizNumber"
                    className="form-input"
                    onChange={this.onChange}
                    name="bizNumber"
                    placeholder="사업자등록번호를 입력해주세요."
                    value={bizNumber}
                    maxLength="40"
                  />
                </label>
              </div>

              <div className="form-group">
                <span className="form-label">사업자등록증</span>
                <div className="file-upload-section">
                  <div className="upload-info">
                    <div className="upload-text">사업자등록증을 업로드하세요</div>
                    {profile.biz_file && (
                      <a
                        className="file-link"
                        href={profile.biz_file}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span role="img" aria-label="문서 파일">
                          📄
                        </span>{' '}
                        업로드된 파일 보기
                      </a>
                    )}
                  </div>
                  <button className="upload-button" type="button" onClick={onUploadBizFile}>
                    파일 선택
                  </button>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button className="submit-button" type="submit">
                회원 정보 수정
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }
}

export default SettingProfile;



// WEBPACK FOOTER //
// ./src/components/settings/SettingProfile/SettingProfile.js